import gql from 'graphql-tag';
import { ENTITY } from '../entities';
import { t } from '../labels';

// Patch mutations + edit queries + introspections of the entity a Company360 can point to.
import { CompanyPatchMutation } from '../../graphql/mutations/CompanyPatchMutation';
import { FoodServicePatchMutation } from '../../graphql/mutations/FoodServicePatchMutation';
import { ServicePatchMutation } from '../../graphql/mutations/ServicePatchMutation';
import { RetailerPatchMutation } from '../../graphql/mutations/RetailerPatchMutation';
import { CompanyEditQuery } from '../../graphql/query/CompanyEditQuery';
import { FoodServiceEditQuery } from '../../graphql/query/FoodServiceEditQuery';
import { ServiceEditQuery } from '../../graphql/query/ServiceEditQuery';
import { RetailerEditQuery } from '../../graphql/query/RetailerEditQuery';
import CompanyIntro from '../../static/introspection/Company.json';
import CompanyPatchIntro from '../../static/introspection/CompanyPatchInput.json';
import FoodServiceIntro from '../../static/introspection/FoodService.json';
import FoodServicePatchIntro from '../../static/introspection/FoodServicePatchInput.json';
import ServiceIntro from '../../static/introspection/Service.json';
import ServicePatchIntro from '../../static/introspection/ServicePatchInput.json';
import RetailerIntro from '../../static/introspection/Retailer.json';
import RetailerPatchIntro from '../../static/introspection/RetailerPatchInput.json';
import { fastTransformer, toHidden } from '../form';
import { toRefInput } from '../dataTrasform';
import { assignAsyncQuery } from '../transformers';
import { generateAsyncQuery, generatePaginatedSelectQuery } from '../graphql';

/**
 * Configurazione per la modifica dei dati dell'entità "azienda" collegata ad un Company360
 * (Company / FoodService / Service / Retailer). Le sezioni di tipo `edit` del dialogo
 * "Dati e relazioni" usano questa configurazione per riusare `EntityEditFetcher` con la
 * mutation di patch e il sottoinsieme di campi giusto per ciascun tipo.
 *
 * Brand è escluso di proposito (richiesta "Brand: aspettare").
 */

export type EditSectionKey = 'info' | 'sede' | 'companyYear';

export interface ConnectedEntityConfig {
	entityName: ENTITY;
	editQuery: any; // fetcher: `entity: node(id) { ... }`
	patchMutation: any;
	typeIntro: any;
	patchInputIntro: any;
	infoFields: string[];
	sedeFields: string[]; // vuoto se la sede non è gestita su questo tipo
	companyYearFkField: string; // nome del campo FK su CompanyYearCreateInput
	hasFairAndCertifications: boolean; // Fiere/Certificazioni: solo Company
}

const COMMON_INFO = ['logoMedia', 'name', 'description', 'descriptionLong', 'website'];
const ADDRESS_FIELDS = ['Country', 'StateProvince', 'postalCode', 'city', 'address1'];

export const CONNECTED_CONFIGS: { [key: string]: ConnectedEntityConfig } = {
	[ENTITY.COMPANY]: {
		entityName: ENTITY.COMPANY,
		editQuery: CompanyEditQuery,
		patchMutation: CompanyPatchMutation,
		typeIntro: CompanyIntro,
		patchInputIntro: CompanyPatchIntro,
		infoFields: [...COMMON_INFO, 'yearEstablished', 'plProducer'],
		sedeFields: ['billingBranch'],
		companyYearFkField: 'Company',
		hasFairAndCertifications: true,
	},
	[ENTITY.FOOD_SERVICE]: {
		entityName: ENTITY.FOOD_SERVICE,
		editQuery: FoodServiceEditQuery,
		patchMutation: FoodServicePatchMutation,
		typeIntro: FoodServiceIntro,
		patchInputIntro: FoodServicePatchIntro,
		infoFields: COMMON_INFO,
		sedeFields: ADDRESS_FIELDS,
		companyYearFkField: 'FoodService',
		hasFairAndCertifications: false,
	},
	[ENTITY.SERVICE]: {
		entityName: ENTITY.SERVICE,
		editQuery: ServiceEditQuery,
		patchMutation: ServicePatchMutation,
		typeIntro: ServiceIntro,
		patchInputIntro: ServicePatchIntro,
		infoFields: COMMON_INFO,
		sedeFields: ADDRESS_FIELDS,
		companyYearFkField: 'Service',
		hasFairAndCertifications: false,
	},
	[ENTITY.RETAILER]: {
		entityName: ENTITY.RETAILER,
		editQuery: RetailerEditQuery,
		patchMutation: RetailerPatchMutation,
		typeIntro: RetailerIntro,
		patchInputIntro: RetailerPatchIntro,
		infoFields: COMMON_INFO,
		sedeFields: ADDRESS_FIELDS,
		companyYearFkField: 'Retailer',
		hasFairAndCertifications: false,
	},
};

/** Mappa il campo polimorfico del Company360 alla config del tipo collegato. */
const CONNECTED_REF_FIELDS: Array<{ field: string; entity: ENTITY }> = [
	{ field: 'Company', entity: ENTITY.COMPANY },
	{ field: 'FoodService', entity: ENTITY.FOOD_SERVICE },
	{ field: 'Service', entity: ENTITY.SERVICE },
	{ field: 'Retailer', entity: ENTITY.RETAILER },
	{ field: 'Brand', entity: ENTITY.BRAND },
];

export interface ConnectedRef {
	config: ConnectedEntityConfig | null; // null per Brand (nessuna sezione edit)
	entityName: ENTITY;
	id: string;
}

/** Determina a quale entità punta il Company360 e ne restituisce id + config (se gestita). */
export const getConnectedRef = (company360: any): ConnectedRef | null => {
	for (const { field, entity } of CONNECTED_REF_FIELDS) {
		const ref = company360 && company360[field];
		if (ref && ref.id) {
			return {
				config: CONNECTED_CONFIGS[entity] || null,
				entityName: entity,
				id: ref.id,
			};
		}
	}
	return null;
};

export const EDIT_SECTION_LABELS: { [k in EditSectionKey]: string } = {
	info: 'CompanyData360/section/info',
	sede: 'CompanyData360/section/sede',
	companyYear: 'CompanyData360/section/companyYear',
};

/** Sezioni di tipo edit disponibili per il tipo collegato. */
export const getEditSectionKeys = (
	config: ConnectedEntityConfig,
): EditSectionKey[] => {
	const keys: EditSectionKey[] = ['info'];
	if (config.sedeFields.length) keys.push('sede');
	keys.push('companyYear');
	return keys;
};

export const editSectionFields = (
	config: ConnectedEntityConfig,
	key: EditSectionKey,
): string[] => (key === 'sede' ? config.sedeFields : config.infoFields);

export const currentYear = (): number => new Date().getFullYear();

/**
 * Query riassuntiva (leggera) dei dati del tipo collegato, per mostrare il testo nelle card
 * delle sezioni edit. Include companyYears per ricavare l'ultimo anno.
 */
export const buildConnectedSummaryQuery = (config: ConnectedEntityConfig) => {
	const isCompany = config.entityName === ENTITY.COMPANY;
	const sede = isCompany
		? 'billingBranch { id name address1 city }'
		: 'address1 city Country { id name }';
	return gql`
		query CompanyData360Summary($id: ID!) {
			entity: node(id: $id) {
				id
				... on ${config.entityName} {
					name
					website
					${sede}
					companyYears { id year revenue numEmployees }
				}
			}
		}
	`;
};

/** Ultimo CompanyYear (per anno) dal risultato della summary query. */
export const latestCompanyYear = (summary: any): any | null => {
	const years = (summary && summary.companyYears) || [];
	if (!years.length) return null;
	return years.reduce((a: any, b: any) => (b.year > a.year ? b : a));
};

const formatNumber = (n: number): string => Number(n).toLocaleString('it-IT');

/** Testo da mostrare nella card di una sezione edit. */
export const editSummaryText = (
	config: ConnectedEntityConfig,
	key: EditSectionKey,
	summary: any,
): string => {
	if (!summary) return '—';
	if (key === 'info') {
		return summary.name || '—';
	}
	if (key === 'sede') {
		if (config.entityName === ENTITY.COMPANY) {
			const b = summary.billingBranch;
			if (!b) return '—';
			const parts = [b.address1, b.city].filter(Boolean);
			return parts.length ? parts.join(', ') : b.name || '—';
		}
		const parts = [summary.address1, summary.city].filter(Boolean);
		return parts.length ? parts.join(', ') : '—';
	}
	// companyYear: gestito a parte (vedi companyYearText) tramite l'anno collegato
	return companyYearText(latestCompanyYear(summary));
};

/** Testo che descrive un CompanyYear (fatturato · dipendenti (anno)), o "—" se assente. */
export const companyYearText = (cy: any): string => {
	if (!cy) return '—';
	const bits: string[] = [];
	if (cy.revenue != null)
		bits.push(`${t`CompanyData360/revenue`}: € ${formatNumber(cy.revenue)}`);
	if (cy.numEmployees != null)
		bits.push(`${t`CompanyData360/numEmployees`}: ${formatNumber(cy.numEmployees)}`);
	return `${bits.join(' · ') || '—'} (${cy.year})`;
};

/** Transformer per creare una FairParticipation dal 360: Company nascosta, ricerca su TUTTE le fiere. */
export const fair360CreateTransformer = (company: any) => (data: any) => {
	const fairQuery = generateAsyncQuery(
		generatePaginatedSelectQuery(ENTITY.FAIR, false),
		true,
		(value: string) => ({ filter: { name: value } }),
	);
	data.fields = fastTransformer({
		Company: toHidden(toRefInput(company)),
		Fair: assignAsyncQuery(fairQuery),
	})(data.fields);
	return data;
};

/** Tutti i CompanyYear dell'entità collegata (per la tabella della sezione). */
export const buildCompanyYearsQuery = (config: ConnectedEntityConfig) => gql`
	query CompanyData360Years($id: ID!) {
		entity: node(id: $id) {
			id
			... on ${config.entityName} {
				companyYears {
					id
					localId
					year
					revenue
					exportRevenue
					ebitda
					numEmployees
				}
			}
		}
	}
`;

/** I CompanyYear attualmente collegati (mostrati) al Company360, con i dati per il riepilogo. */
export const LINKED_COMPANY_YEARS_QUERY = gql`
	query Relation360CompanyYearsFull($id: ID!) {
		entity: node(id: $id) {
			id
			... on Company360 {
				Relation360_CompanyYears {
					id
					localId
					year
					revenue
					numEmployees
				}
			}
		}
	}
`;
